import { Component, Input, OnDestroy, OnInit, Output, EventEmitter } from '@angular/core';
import { Subscription } from 'rxjs';
import { KngTable, KngTableState, TableCategory, AprRejBtnType, AprRejUserData, AprRejActionData, GridValue, ApproveUserData } from './definitions';
import { KngTableService } from './kng-table.service';
import { AllDataType } from '../../shared/shared.interface';

@Component({
	selector: 'kng-table',
	templateUrl: './template/kng-table.component.html',
	styleUrls: ['./template/kng-table.component.scss']
})
export class KngTableComponent implements KngTable, OnInit, OnDestroy 
{
	private subscriptions: Subscription = new Subscription();
	tableCategory = TableCategory;
	aprRejBtnType = AprRejBtnType;
	isLinkEnabled: boolean = false;
	allSelected: boolean = false;

	@Input() set state(value: KngTableState) 
	{
		this.service.updateState(value);
	}
	get state(): KngTableState 
	{
		return this.service.state;
	}

	@Output() stateChanged: EventEmitter<KngTableState> = new EventEmitter<KngTableState>();
	@Output() btnClickEvent: EventEmitter<AprRejActionData> = new EventEmitter<AprRejActionData>();
	@Output() navigateToApproveNewUser: EventEmitter<string> = new EventEmitter<string>();
	@Output() tooltipClicked: EventEmitter<string> = new EventEmitter<string>();
	@Output() editButtonClicked: EventEmitter<string> = new EventEmitter<string>();
	@Output() locationListButton: EventEmitter<string> = new EventEmitter<string>();
	@Output() uploadFileLinkClicked: EventEmitter<GridValue> = new EventEmitter<GridValue>();

	constructor(private service: KngTableService) { }

	ngOnInit(): void 
	{
		this.initSubscriptions();
		this.setAll(false);
	}
	ngOnDestroy(): void 
	{
		this.destroySubscriptions();
	}
	private initSubscriptions(): void 
	{
		const stateChangeSubs: Subscription = this.service.stateChanged.subscribe((next: KngTableState) => this.stateChanged.emit(next));
		this.subscriptions.add(stateChangeSubs);
	}
	private destroySubscriptions(): void 
	{
		this.subscriptions.unsubscribe();
	}
	updateAllSelected() 
	{
		this.allSelected = this.state.gridData.every(row => row.checked);
		if (this.state.gridData.length >= 10) 
		{
			this.allSelected = this.state.gridData.filter(row => row.checked)?.length >= 10;
		}
	}
	partiallySelected(): boolean 
	{
		if (this.state.gridData.every(row => !row.checked)) 
		{
			this.allSelected = false;
		}
		if (this.state.gridData.filter(t => t.checked).length >= 10 && this.state.ribbon.ctaList.length > 0) 
		{
			this.state.gridData.filter(t => !t.checked).forEach(checkbox => checkbox.disabled = true);
		}
		else 
		{
			this.state.gridData.forEach(checkbox => checkbox.disabled = false);
		}
		return this.state.gridData.filter(t => t.checked).length > 0 && !this.allSelected;
	}
	selectedCount(): boolean 
	{
		return this.state.gridData.filter(t => t.checked).length > 0;
	}
	setAll(completed: boolean) 
	{
		this.allSelected = completed;
		this.state.gridData.slice(0, 10).forEach(t => t.checked = completed);
		this.state.gridData.slice(10, this.state.gridData.length).forEach(t => { t.disabled = completed; t.checked = false; });
	}
	countOfSelectedRequests() 
	{
		const countOfSelectedRequests = this.state.gridData.filter(t => t.checked).length;
		return countOfSelectedRequests + ' ' + this.state.itemsSelected;
	}
	cancelSelection() 
	{
		this.state.gridData.forEach((row) => { row.checked = false; });
		this.allSelected = false;
	}
	public convertStringToDate(data: AllDataType): Date | string 
	{
		return data && typeof (data) !== 'boolean' ? new Date(data) : '';
	}
	rejectSingleUser(data: GridValue | undefined) 
	{
		const selectedRowDataArr: AprRejUserData[] = [];
		if (data) 
		{
			const selectedRowEntry: AprRejUserData = {
				id: data.id ? data.id : '',
				email: data.email ? data.email : '',
				state: data.state ? data.state : ''
			};
			selectedRowDataArr.push(selectedRowEntry);
		}
		return selectedRowDataArr;
	}
	rejectMultipleUser(): AprRejUserData[] 
	{
		const selectedRowDataArr: AprRejUserData[] = [];
		this.state.gridData.filter(t => t.checked).forEach((row) => 
		{
			const selectedRowEntry: AprRejUserData = {
				id: row.id ? row.id : '',
				email: row.email ? row.email : '',
				state: row.state ? row.state : ''
			};
			selectedRowDataArr.push(selectedRowEntry);
		});
		return selectedRowDataArr;
	}
	approveSingleUser(data: GridValue | undefined) 
	{
		const selectedRowDataArr: ApproveUserData[] = [];
		if (data) 
		{
			const selectedRowEntry: ApproveUserData = {
				id: data.id,
				email: data.email ? data.email : '',
				state: data.state ? data.state : '',
				portalUserId: data.portalUserId ? data.portalUserId : '',
				company: data.company,
				roles: data.roles,
				portalApprovedCustomerID: data.portalApprovedCustomerID,
				tempIsPrefilled: data.tempIsPrefilled ? data.tempIsPrefilled : false,
				phoneNumber: data.phoneNumber,
				roleName: data.roleName,
				adminLocationID: data.adminLocationID
			};
			selectedRowDataArr.push(selectedRowEntry);
		}
		return selectedRowDataArr;
	}
	approveMultipleUser(): ApproveUserData[] 
	{
		const selectedRowDataArr: ApproveUserData[] = [];
		this.state.gridData.filter(t => t.checked).forEach((row) => 
		{
			const selectedRowEntry: ApproveUserData = {
				id: row.id,
				email: row.email ? row.email : '',
				state: row.state ? row.state : '',
				portalUserId: row.portalUserId ? row.portalUserId : '',
				company: row.company,
				roles: row.roles,
				portalApprovedCustomerID: row.portalApprovedCustomerID,
				tempIsPrefilled: row.tempIsPrefilled ? row.tempIsPrefilled : false,
				phoneNumber: row.phoneNumber,
				roleName: row.roleName,
				adminLocationID: row.adminLocationID ? row.adminLocationID : ''
			};
			selectedRowDataArr.push(selectedRowEntry);
		});
		return selectedRowDataArr;
	}
	emitClickEvent(btnType: AprRejBtnType | string, data?: GridValue) 
	{
		const selectedActionData: AprRejActionData = {
			action: btnType,
			selectedUserData: [],
			selectedUserDataForApprove: []
		};
		if (btnType === this.aprRejBtnType.singleReject) 
		{
			selectedActionData.selectedUserData = this.rejectSingleUser(data);
			this.cancelSelection();
		}
		else if (btnType === this.aprRejBtnType.bulkReject) 
		{
			selectedActionData.selectedUserData = this.rejectMultipleUser();
		}
		else if (btnType === this.aprRejBtnType.singleApprove) 
		{
			selectedActionData.selectedUserDataForApprove = this.approveSingleUser(data);
			this.cancelSelection();
		}
		else if (btnType === this.aprRejBtnType.bulkApprove) 
		{
			selectedActionData.selectedUserDataForApprove = this.approveMultipleUser();
		}
		this.btnClickEvent.emit(selectedActionData);
	}
	redirectToApproveNewUserPage(userId: string): void 
	{
		if (userId) 
		{
			this.navigateToApproveNewUser.emit(userId);
		}
	}
	showTooltip(id: string) 
	{
		this.tooltipClicked.emit(id);
	}
	openEditCompanyDetailsModal(id: string) 
	{
		this.editButtonClicked.emit(id);
	}
	getLocationList(id: string) 
	{
		this.locationListButton.emit(id);
	}
	openUploadFileModal(row: GridValue, i: number)
	{
		if(row.values[i].icon?.iconPath !== 'assets/uploadDisabled.svg')
			this.uploadFileLinkClicked.emit(row);
	}
}
